import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../components/ui/Modal'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Avatar } from '../../components/ui/Avatar'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { PermissionGate } from '../../components/PermissionGate'
import { useAuth } from '../../auth/AuthContext'
import { chatApi } from '../../api/chat.api'
import { usersApi } from '../../api/users.api'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'
import formStyles from '../../components/ui/Form.module.css'

export function ChannelMembersModal({
  channelId,
  channelName,
  onClose,
  onDeleted,
}: {
  channelId: string
  channelName: string
  onClose: () => void
  onDeleted: () => void
}) {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('chat.manage_channels')
  const queryClient = useQueryClient()
  const [selectedIds, setSelectedIds] = useState<string[] | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const membersQuery = useQuery({ queryKey: ['chat', channelId, 'members'], queryFn: async () => (await chatApi.listMembers(channelId)).data })
  const usersQuery = useQuery({ queryKey: ['users', { pageSize: 100 }], queryFn: async () => (await usersApi.list({ pageSize: 100 })).data, enabled: canManage })

  useEffect(() => {
    if (membersQuery.data && selectedIds === null) {
      setSelectedIds(membersQuery.data.map((m) => m.userId))
    }
  }, [membersQuery.data, selectedIds])

  const saveMutation = useMutation({
    mutationFn: () => chatApi.setMembers(channelId, selectedIds ?? []),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat', channelId, 'members'] })
      toast.success('Mitglieder aktualisiert.')
      onClose()
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => chatApi.deleteChannel(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat'], exact: false })
      toast.success(`Channel #${channelName} gelöscht.`)
      onDeleted()
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Löschen fehlgeschlagen.'),
  })

  return (
    <Modal
      title={`Mitglieder — #${channelName}`}
      onClose={onClose}
      footer={
        canManage ? (
          <>
            <Button variant="danger" onClick={() => setConfirmDelete(true)} style={{ marginRight: 'auto' }}>
              Channel löschen
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button variant="primary" onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
              Speichern
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
        )
      }
    >
      {!canManage ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {membersQuery.data?.map((m) => (
            <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <Avatar name={m.user.displayName} imageUrl={m.user.avatarUrl} status={m.user.status} />
              <span style={{ fontSize: 'var(--text-sm)' }}>{m.user.displayName}</span>
            </div>
          ))}
          {membersQuery.data?.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Noch keine Mitglieder.</p>
          ) : null}
        </div>
      ) : (
        <PermissionGate anyOf={['chat.manage_channels']}>
          <Field label="Mitglieder" hint="Auswahl gilt sofort nach dem Speichern — abgewählte Mitglieder verlieren bei privaten Channels den Zugriff.">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {usersQuery.data?.map((user) => {
                const checked = (selectedIds ?? []).includes(user.id)
                return (
                  <label
                    key={user.id}
                    className={formStyles.checkboxRow}
                    style={{ border: '1px solid var(--surface-border)', borderRadius: 'var(--radius-md)', padding: '4px 10px' }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const current = selectedIds ?? []
                        setSelectedIds(checked ? current.filter((id) => id !== user.id) : [...current, user.id])
                      }}
                    />
                    {user.displayName}
                  </label>
                )
              })}
            </div>
          </Field>
        </PermissionGate>
      )}

      {confirmDelete ? (
        <ConfirmDialog
          title="Channel löschen"
          message={`"#${channelName}" wirklich löschen? Alle Nachrichten und Reaktionen darin gehen dabei unwiderruflich verloren.`}
          danger
          loading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
    </Modal>
  )
}
